import type CargoUnit from "@/domains/CargoUnits/CargoUnit";
import type AbstractShipmentDocument from "@/domains/Abstract/AbstractShipmentDocument";
import type Partner from '@/domains/Partners/Partner';
import type Store from '@/domains/Stores/Store';
import type IntRange from '@/core/types/IntRange';
import type Dictionary from '@/core/types/Dictionary';
import type Company from "@/domains/Companies/Company";
import BaseEntity from '@/core/misc/BaseEntity';
import Assembly from '@/domains/Assemblies/Assembly';
import Shipment from '@/domains/Shipments/Shipment';
import OrderPosition from '@/domains/Orders/OrderPosition';
import Product from '@/domains/Products/Product';
import TransportOrder from '@/domains/TransportOrders/TransportOrder';
import Core from "@/core/Core";
import Sum from "@/core/types/Sum";
import Qty from "@/core/types/Qty";
import Unit from "@/domains/Units/Unit";


class TransportOrderPosition extends BaseEntity {

  /* ID документа-основания */
  public documentId: string;

  public documentClass: string; // 'Assembly'|'Shipment'|'TransportOrder'|'OrderPosition'|'Product'
  // Леша сделал такой контракт, они нужны ему, фронту они не нужны
  get positionType(): string {return this.documentClass}
  // ---- Документ основания ----
  // Перед созданием позиции запрашивается полностью и присваивается сюда.
  // Тип зависит от documentClass: Assembly | Shipment | TransportOrder | OrderPosition | Product

  public _document: Assembly | Shipment | TransportOrder | OrderPosition | Product;

  // ---- qty — для Product и OrderPosition, unit — только для Product ----
  // Для остальных типов количество и единица берутся из документа основания.
  // Для Product отдельного документа с этими полями нет, поэтому храним явно.
  // Для OrderPosition везём не обязательно всё заказанное (например, возвращаем часть),
  // поэтому количество к перевозке храним в позиции ЗТК, а не берём из позиции заказа.

  public qty: Qty | null;
  public unit: Unit | null;

  public storeTemperatureRange: IntRange;

  // todo я подозреваю что это неправильно, но модель мне запретили трогать
  get sender(): Partner | undefined {
    switch (this.documentClass) {
      case 'Assembly':
      case 'Shipment':
        return (this.document as AbstractShipmentDocument)?.seller;
      case 'TransportOrder':
        return (this.document as TransportOrder)?.sender;
      case 'OrderPosition':
        return (this.document as OrderPosition)?.seller;
      case 'Product':
        return undefined;
    }
    return undefined;
  }

  // todo я подозреваю что это неправильно, но модель мне запретили трогать
  get senderCompany(): Company | undefined {
    switch (this.documentClass) {
      case 'Assembly':
      case 'Shipment':
        return (this.document as AbstractShipmentDocument)?.sellerCompany;
      case 'TransportOrder':
        return (this.document as TransportOrder)?.senderCompany;
      case 'OrderPosition':
        return (this.document as OrderPosition)?.sellerCompany;
      case 'Product':
        return undefined;
    }
    return undefined;
  }

  // todo я подозреваю что это неправильно, но модель мне запретили трогать
  get senderStore(): Store | undefined {
    switch (this.documentClass) {
      case 'Assembly':
      case 'Shipment':
        return (this.document as AbstractShipmentDocument)?.sellerStore;
      case 'TransportOrder':
        return (this.document as TransportOrder)?.senderStore;
      case 'OrderPosition':
        return (this.document as OrderPosition)?.sellerStore;
      case 'Product':
        return undefined;
    }
    return undefined;
  }

  // todo я подозреваю что это неправильно, но модель мне запретили трогать
  get receiverStore(): Store | undefined {
    switch (this.documentClass) {
      case 'Assembly':
      case 'Shipment':
        return (this.document as AbstractShipmentDocument)?.buyerStore;
      case 'TransportOrder':
        return (this.document as TransportOrder)?.receiverStore;
      case 'OrderPosition':
        return (this.document as OrderPosition)?.buyerStore;
      case 'Product':
        return undefined;
    }
    return undefined;
  }

  /**
   * Позиции документа основания — для двухуровневого отображения в таблице.
   * Assembly → AssemblyPosition[]
   * Shipment → ShipmentPosition[]
   * TransportOrder → TransportOrderPosition[]
   * OrderPosition, Product — позиций нет.
   */
  get positions(): any[] {
    switch (this.documentClass) {
      case 'Assembly':
      case 'Shipment':
      case 'TransportOrder':
        return (this.document as any)?.positions ?? [];
      case 'OrderPosition':
      case 'Product':
        return [];
    }
    return [];
  }

  async getDeclaredCost(): Promise<Sum>  {
    switch (this.documentClass) {
      case 'Shipment':       return (this.document as Shipment)?.accountingSumFullCalculated!;
      // Не sumFullSeller позиции заказа: везём количество позиции ЗТК, оно может быть меньше заказанного
      case 'OrderPosition':  return (this.document as OrderPosition).priceFullSeller.mul(this.qty!);
      case 'TransportOrder': return (this.document as TransportOrder).declaredCost;
      case 'Assembly':       return await (this.document as Assembly).getDeclaredCost();
      case 'Product':        return Sum.zero();
    }
  }

  get declaredCostCurrencyCode(): string {
    switch (this.documentClass) {
      case 'Shipment':       return (this.document as Shipment)?.accountingCurrencyCode!;
      case 'OrderPosition':  return (this.document as OrderPosition).currencyCode!;
      case 'TransportOrder': return (this.document as TransportOrder).declaredCostCurrencyCode;
      case 'Assembly':       return (this.document as Assembly).accountingCurrencyCode;
      case 'Product':        return Core.acl.currentPartnerCurrencyCode;
    }
  }


  get cargoUnits(): Dictionary<string, CargoUnit> {
    switch (this.documentClass) {
      case 'Assembly':
      case 'Shipment':
      case 'TransportOrder':
        return (this.document as any)?.cargoUnits;
      case 'OrderPosition':
      case 'Product':
        return [];
    }
    return [];
  }

  get document(): Assembly | Shipment | TransportOrder | OrderPosition | Product {
    return this._document;
  }

  set document(value: Assembly | Shipment | TransportOrder | OrderPosition | Product) {
    this._document = value;
    this.documentId = String(value.id);
  }

  // метод удалить нельзя, он переиспользуется и он удобен
  static fromDocument(document: Assembly | Shipment | TransportOrder | OrderPosition | Product): TransportOrderPosition {
    if (document instanceof Assembly) {
      return TransportOrderPosition.fromAssembly(document);
    }
    if (document instanceof Shipment) {
      return TransportOrderPosition.fromShipment(document);
    }
    if (document instanceof TransportOrder) {
      return TransportOrderPosition.fromTransportOrder(document);
    }
    if (document instanceof OrderPosition) {
      return TransportOrderPosition.fromOrderPosition(document);
    }
    if (document instanceof Product) {
      return TransportOrderPosition.fromProduct(document);
    }

    throw new TypeError('TransportOrderPosition.fromDocument: неизвестный тип документа');
  }

  static fromAssembly(assembly: Assembly): TransportOrderPosition {
    const position = new TransportOrderPosition();

    position.documentClass = 'Assembly';
    position.document = assembly;
    position.storeTemperatureRange = assembly.storeTemperatureRange;

    return position;
  }

  static fromShipment(shipment: Shipment): TransportOrderPosition {
    const position = new TransportOrderPosition();

    position.documentClass = 'Shipment';
    position.document = shipment;
    position.storeTemperatureRange = shipment.storeTemperatureRange;

    return position;
  }

  static fromTransportOrder(transportOrder: TransportOrder): TransportOrderPosition {
    const position = new TransportOrderPosition();

    position.documentClass = 'TransportOrder';
    position.document = transportOrder;
    position.storeTemperatureRange = transportOrder.storeTemperatureRange;

    return position;
  }

  static fromOrderPosition(orderPosition: OrderPosition): TransportOrderPosition {
    const position = new TransportOrderPosition();

    position.documentClass = 'OrderPosition';
    position.document = orderPosition;
    position.storeTemperatureRange = orderPosition.storeTemperatureRange;

    position.qty = orderPosition.qty;

    return position;
  }

  static fromProduct(product: Product): TransportOrderPosition {
    const position = new TransportOrderPosition();

    position.documentClass = 'Product';
    position.document = product;
    position.unit = product.unit;

    return position;
  }

  static fromServer(data: any, result: TransportOrderPosition = new TransportOrderPosition()): TransportOrderPosition {
    super.fromServer(data, result);

    result.documentClass = data.documentClass;

    // qty — для Product и OrderPosition
    if (result.documentClass === 'Product' || result.documentClass === 'OrderPosition') {
      result.qty = Core.utils.hydrate(data.qty, Qty.fromServer)!;
    }

    // unit — только для Product
    if (result.documentClass === 'Product') {
      result.unit = Core.utils.hydrate(data.unit, Unit.fromServer)!;
    }

    // Гидрация документа основания через конструктор соответствующей сущности
    if (data.document) {
      switch (data.documentClass) {
        case 'Assembly':
          result.document = Assembly.fromServer(data.document);
          break;
        case 'Shipment':
          result.document = Shipment.fromServer(data.document);
          break;
        case 'TransportOrder':
          result.document = TransportOrder.fromServer(data.document);
          break;
        case 'OrderPosition':
          result.document = OrderPosition.fromServer(data.document);
          break;
        case 'Product':
          result.document = Product.fromServer(data.document);
          break;
      }
    }

    return result;
  }
}

export default TransportOrderPosition;